import { BehaviorSubject, Subject } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { streem, Long, Timestamp } from 'streem-sdk-protobuf';
import { invariant } from '@streem/toolbox';
import { firstFromGroup } from '../../util/rx/first_from_group';
import { Wall } from './wall';
import { sortClientCreatedAt } from '../../util/rx/sort_client_created_at';
import { findInGroup, accumulateAndMapWallEvents } from './utils';
import { logger } from '../../util/logger';
import { RepeatingTask } from '../../util/repeating_task';
let heartbeatCount = 0;
export class UserWall extends Wall {
    constructor(config) {
        super(config);
        this.unsubscribe = new Subject();
        this.currentInviteSubject = new BehaviorSubject(undefined);
        this.currentPendingInvite = this.currentInviteSubject.asObservable();
        logger.info('Creating UserWall...');
        this.userDeviceUpdates = this.itemsByType.pipe(findInGroup('lastIdentifiedDevice'), accumulateAndMapWallEvents('lastIdentifiedDevice'), sortClientCreatedAt(), shareReplay(1));
        this.smsDeliveryStatuses = this.itemsByType.pipe(findInGroup('smsDeliveryStatus'), accumulateAndMapWallEvents('smsDeliveryStatus'), sortClientCreatedAt(), shareReplay(1));
        this.userAvailabilityStatus = this.itemsByType.pipe(findInGroup('userAvailability'), accumulateAndMapWallEvents('userAvailability'), sortClientCreatedAt(), firstFromGroup(), shareReplay(1));
        this.groupReservations = this.itemsByType.pipe(findInGroup('lastGroupReservation'), accumulateAndMapWallEvents('lastGroupReservation'), sortClientCreatedAt(), firstFromGroup(), shareReplay(1));
        /**
         * Listen for current pending invites
         * and publish the current pending invite when it's found
         */
        this.getCurrentPendingInvites(this.getAllRoomInvitations())
            .pipe(firstFromGroup(), takeUntil(this.unsubscribe))
            .subscribe((nextInvite) => {
            if (nextInvite?.id !== this.currentInviteSubject.value?.id) {
                logger.info(`UserWall received invitation invitationId: ${nextInvite?.id} with status: ${nextInvite?.status}`, nextInvite);
                this.currentInviteSubject.next(nextInvite);
            }
        });
        // Heartbeat task to ensure Twilio Sync connection is alive
        this.heartBeatTask = new RepeatingTask({
            taskName: UserWall.HEART_BEAT_TASK_NAME,
            intervalMs: UserWall.HEART_BEAT_INTERVAL_MS,
            errorThreshold: UserWall.HEART_BEAT_ERROR_THRESHOLD,
            task: async () => {
                let connectionState = this.wallSyncClient.wallSyncConnectionState;
                if (this.tokenExpired === true) {
                    logger.warn(`UserWall not sending heartbeat due to expired SyncClient token`);
                    return;
                }
                if (connectionState === 'disconnected') {
                    logger.warn(`UserWall SyncClient not in connected state. Will try to reconnect. connectionState=${connectionState}`);
                    await this.wallSyncClient.reconnectIfNotConnected();
                    connectionState = this.wallSyncClient.wallSyncConnectionState;
                }
                if (connectionState !== 'connected') {
                    logger.warn(`UserWall skipping the sending of heartbeat because SyncClient is not in 'connected' state; current connectionState=${connectionState}`);
                    return;
                }
                const currentTime = new Date();
                const heartbeatWallItem = new streem.user.UserWallItem({
                    id: `heartbeat`,
                    ownerDeviceId: this.config.localDeviceId.toString(),
                    heartbeat: {
                        timestamp: this.getProtobufTimestamp(currentTime),
                        count: ++heartbeatCount,
                    },
                });
                logger.debug(`UserWall Sending Wall Heartbeat... wallItemId=${heartbeatWallItem.id} count=${heartbeatWallItem.heartbeat?.count}`);
                this.addOrUpdate(heartbeatWallItem);
            },
            onError: err => logger.error(`taskName=${UserWall.HEART_BEAT_TASK_NAME} userId=${config.localUserId} encountered error=${err}`),
        });
    }
    async connect() {
        logger.info(`UserWall connecting...`);
        await super.connect();
        logger.info('Starting UserWall heartbeat task...');
        await this.heartBeatTask?.start();
    }
    async disconnect() {
        logger.info('stopping UserWall heartbeat task... HEART_BEAT_INTERVAL_MS=$HEART_BEAT_INTERVAL_MS');
        this.heartBeatTask.stop();
        this.unsubscribe.next(undefined);
        logger.info(`UserWall disconnecting...`);
        this.connectedSubscription?.unsubscribe();
        await super.disconnect();
    }
    /**
     * Finds all room invitations on the user wall and sorts them from newest to oldest
     */
    getAllRoomInvitations() {
        return this.itemsByType.pipe(findInGroup('roomInvitation'), accumulateAndMapWallEvents('roomInvitation'), sortClientCreatedAt(), shareReplay(1));
    }
    /**
     * Returns an observable of room invitations that are pending and not expired
     * @param invites Invites from the UserWall
     */
    getCurrentPendingInvites(invites) {
        return invites.pipe(map(invites => invites.filter(i => {
            // No date? Get out of here
            if (!i.clientCreatedAt)
                return false;
            // Not pending? Get out of here
            if (i.status !== streem.api.RoomInvitation.Status.STATUS_PENDING)
                return false;
            invariant(i?.clientCreatedAt?.seconds?.low, 'Invite clientCreatedAt missing "seconds" property');
            // Is the invite older than 48 hours (in seconds)?
            const FORTY_EIGHT_HOURS = 60 * 60 * 48;
            const inviteIsExpired = new Long(new Date().getTime() / 1000)
                .sub(i.clientCreatedAt.seconds)
                .greaterThan(FORTY_EIGHT_HOURS);
            return !inviteIsExpired;
        })));
    }
    getProtobufTimestamp(date) {
        const millis = date.getTime();
        return new Timestamp({
            seconds: new Long(millis / 1000),
            nanos: (millis % 1000) * 1e6,
        });
    }
}
UserWall.HEART_BEAT_TASK_NAME = 'UserWall_HeartbeatTask';
UserWall.HEART_BEAT_INTERVAL_MS = 1000 * 30 * 1; // 30 seconds
UserWall.HEART_BEAT_ERROR_THRESHOLD = 30;
//# sourceMappingURL=user_wall.js.map